import { Suspense as Island } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Img } from "@/components/Img";
import { CtaLink, SectionHead } from "@/components/Ui";
import { Pillars, SupportList, MarketGrowth, InvestmentSnapshot } from "@/components/Blocks";
import { ProcessSteps } from "@/components/ProcessSteps";
import { Reviews } from "@/components/Reviews";
import { TerritoryCta } from "@/components/CtaBands";
import { ContactSection } from "@/components/ContactSection";
import { ABOUT, ADVANTAGE, PROOF, SUPPORT } from "@/content/site";

const Hero = () => (
  <section className="bg-ivory" aria-labelledby="hero-heading" data-testid="home-hero">
    <div className="wrap grid items-center gap-10 py-12 md:py-16 lg:grid-cols-12 lg:gap-14 lg:py-20">
      <div className="lg:col-span-6">
        <p className="eyebrow rise mb-6">VidaVital Medical Franchise</p>
        <h1 id="hero-heading" className="rise rise-2 text-4xl font-bold sm:text-5xl lg:text-6xl">Own the Future of Personalized Wellness</h1>
        <p className="rise rise-3 mt-6 max-w-xl text-lg text-stone-600 md:text-xl">A Proven, Turnkey System for Building a Concierge Wellness Clinic</p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <CtaLink id="hero-explore-ownership">Explore Ownership</CtaLink>
          <CtaLink id="hero-review-requirements" variant="line" arrow={false}>Review Ownership Requirements</CtaLink>
        </div>
        <ul className="mt-12 grid grid-cols-3 border-y border-line" data-testid="hero-proof-points">
          {PROOF.map((p, i) => (
            <li key={p} className={`py-5 pr-3 font-display text-sm font-semibold leading-snug sm:text-base ${i ? "border-l border-line pl-4" : ""}`}>{p}</li>
          ))}
        </ul>
      </div>
      <div className="relative lg:col-span-6">
        <div className="absolute -right-3 -top-3 hidden h-24 w-24 border-r-2 border-t-2 border-gold lg:block" aria-hidden="true" />
        <Img name="clinician-staff" priority sizes="(min-width: 1024px) 46vw, 100vw" alt="VidaVital Medical clinician in a white coat reviewing a tablet with a team member in front of the gold VidaVital logo" imgClassName="aspect-[4/3] w-full object-cover" />
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="section bg-white" aria-labelledby="about-heading">
    <div className="wrap grid gap-12 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-6 lg:order-2">
        <SectionHead eyebrow={ABOUT.eyebrow} title={ABOUT.title} id="about-heading">
          <p>{ABOUT.p1}</p>
          <p className="font-semibold italic text-ink">{ABOUT.p2}</p>
        </SectionHead>
        <div className="mt-9"><CtaLink id="about-explore-franchise">Explore the Franchise</CtaLink></div>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:col-span-6 lg:order-1">
        <Img name="team-entry" sizes="(min-width: 1024px) 23vw, 50vw" alt="VidaVital Medical team members standing inside the clinic" imgClassName="aspect-[3/4] w-full object-cover" />
        <Img name="clinicians-equipment" sizes="(min-width: 1024px) 23vw, 50vw" alt="VidaVital Medical clinicians using medical equipment in a treatment room" imgClassName="mt-10 aspect-[3/4] w-full object-cover" />
      </div>
    </div>
  </section>
);

const Advantage = () => (
  <section className="section on-dark bg-ink text-ivory" aria-labelledby="advantage-heading">
    <div className="wrap">
      <SectionHead eyebrow={ADVANTAGE.eyebrow} title={<span className="text-ivory">{ADVANTAGE.title}</span>} id="advantage-heading">
        <p>{ADVANTAGE.p1}</p>
        <p>{ADVANTAGE.p2}</p>
      </SectionHead>
      <div className="mt-12"><Pillars items={ADVANTAGE.pillars} dark testId="advantage-pillars" /></div>
      <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
        <CtaLink to="/contact?intent=overview" id="advantage-request-overview">Request the Franchise Overview</CtaLink>
        <Link to="/opportunity" className="text-link" data-testid="advantage-opportunity-link">Explore the full franchise opportunity</Link>
      </div>
    </div>
  </section>
);

const Support = () => (
  <section className="section bg-ivory" aria-labelledby="support-heading">
    <div className="wrap grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow={SUPPORT.eyebrow} title={SUPPORT.title} id="support-heading"><p>{SUPPORT.p}</p></SectionHead>
        <p className="mt-6 font-display text-lg font-semibold">{SUPPORT.focus}</p>
      </div>
      <div className="lg:col-span-7">
        <SupportList />
        <p className="mt-8 font-semibold">{SUPPORT.promise}</p>
        <Link to="/investment-support" className="text-link mt-6 inline-block" data-testid="support-details-link">See franchise investment and support details</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo path="/" />
      <Hero />
      <Island fallback={null}><About /></Island>
      <Island fallback={null}><Advantage /></Island>
      <Island fallback={null}><Support /></Island>
      <Island fallback={null}>
      <section className="section bg-white" aria-label="Market growth"><div className="wrap">
        <MarketGrowth />
        <Link to="/opportunity" className="text-link mt-8 inline-block" data-testid="market-opportunity-link">Read why now is the market opportunity</Link>
      </div></section>
      <section className="section on-dark bg-ink text-ivory"><div className="wrap">
        <InvestmentSnapshot />
        <Link to="/investment-support" className="text-link mt-8 inline-block" data-testid="investment-details-link">Review investment and support details</Link>
      </div></section>
      <section className="section bg-ivory" aria-labelledby="process-heading"><div className="wrap">
        <SectionHead eyebrow="The Franchise Process" title="Franchising Made Simple" id="process-heading" />
        <div className="mt-10"><ProcessSteps compact /></div>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
          <CtaLink id="process-explore-franchise">Explore the Franchise</CtaLink>
          <Link to="/process" className="text-link" data-testid="process-details-link">See all five franchise process steps</Link>
        </div>
      </div></section>
      </Island>
      <Island fallback={null}><Reviews /></Island>
      <Island fallback={null}><TerritoryCta /></Island>
      <Island fallback={null}><ContactSection /></Island>
    </>
  );
}
